// StockAlertBanner - Simple and intuitive stock alert
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { AlertTriangle, X, ShoppingCart, ChevronRight } from 'lucide-react';

const StockAlertBanner = () => {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [alerts, setAlerts] = useState(null);
    const [dismissed, setDismissed] = useState(() => {
        // Check if dismissed in this session
        return sessionStorage.getItem('stockAlertDismissed') === 'true';
    });

    const fetchAlerts = useCallback(async () => {
        try {
            const response = await api().get('/pos/stock-alerts/critical');
            setAlerts(response.data);
        } catch (error) {
            console.error('Error fetching stock alerts:', error);
        }
    }, [api]);

    useEffect(() => {
        fetchAlerts();
        // Refresh every 60 seconds
        const interval = setInterval(fetchAlerts, 60 * 1000);
        return () => clearInterval(interval);
    }, [fetchAlerts]);

    const handleDismiss = () => {
        setDismissed(true);
        sessionStorage.setItem('stockAlertDismissed', 'true');
    };

    // Don't show if no alerts or dismissed
    if (!alerts || alerts.total_alerts === 0 || dismissed) {
        return null;
    }

    const hasCritical = alerts.critical_count > 0;
    const totalCount = alerts.total_alerts;
    
    // Get top 3 products to display
    const topProducts = [
        ...(alerts.critical_products || []),
        ...(alerts.low_products || [])
    ].slice(0, 3);

    const productNames = topProducts.map(p => p.name).join(', ');

    return (
        <div 
            className={`rounded-lg mb-4 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 relative z-10 ${
                hasCritical 
                    ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' 
                    : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
            }`}
            data-testid="stock-alert-banner"
        >
            {/* Icon + Message */}
            <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className={`p-2 rounded-full flex-shrink-0 ${
                    hasCritical ? 'bg-red-100 dark:bg-red-800/30' : 'bg-amber-100 dark:bg-amber-800/30'
                }`}>
                    <AlertTriangle className={`w-4 h-4 sm:w-5 sm:h-5 ${
                        hasCritical ? 'text-red-600' : 'text-amber-600'
                    }`} />
                </div>
                
                <div className="flex-1 min-w-0">
                    {/* Main message */}
                    <p className={`font-medium text-sm sm:text-base ${
                        hasCritical ? 'text-red-800 dark:text-red-200' : 'text-amber-800 dark:text-amber-200'
                    }`}>
                        {hasCritical 
                            ? `${alerts.critical_count} produit${alerts.critical_count > 1 ? 's' : ''} en rupture`
                            : `${totalCount} produit${totalCount > 1 ? 's' : ''} à réapprovisionner`
                        }
                    </p>
                    
                    {/* Product list preview */}
                    <p className="text-xs sm:text-sm text-muted-foreground truncate mt-0.5">
                        {productNames}{totalCount > 3 ? ` et ${totalCount - 3} autres...` : ''}
                    </p>
                </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 sm:flex-shrink-0">
                <Button
                    size="sm"
                    onClick={() => navigate('/stock-movements')}
                    className={`text-xs sm:text-sm h-8 sm:h-9 ${
                        hasCritical 
                            ? 'bg-red-600 hover:bg-red-700 text-white' 
                            : 'bg-amber-600 hover:bg-amber-700 text-white'
                    }`}
                >
                    <ShoppingCart className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5" />
                    <span className="hidden sm:inline">Gérer le stock</span>
                    <span className="sm:hidden">Stock</span>
                    <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
                
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="h-8 w-8 sm:h-9 sm:w-9 rounded-md flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    title="Masquer pour cette session"
                    data-testid="dismiss-stock-alert"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>
        </div>
    );
};

export default StockAlertBanner;
